import {
  FETCH_DELIVERY_TIME,
  DELIVERY_TIME_LOADING,
} from "../actions/actionTypes";
import moment from "moment";
import { REHYDRATE } from "redux-persist";
import {
  hasFulfillmentLocationChanged,
  normalizeFulfillmentContext,
} from "../../checkout-v2/domain/fulfillmentContext";

const initialState = {
  asap: null,
  scheduled: null,
  pickup: null,

  expected_date: "",
  expected_start: "",
  expected_end: "",
  within: null,
  authority: "",
  type: null,
  isPickup: false,

  loading: false,

  drawerOpen: false,
  didOpen: false,

  showCheckCart: false,
};

const CART_CONTENT_ACTIONS = new Set([
  "ADD_To_CART",
  "ADD_PROMO_ITEM",
  "CLEAR_CART",
  "EDIT_PRODUCT",
  "EDIT_QUANTITY",
  "EDIT_QUANTITY_PROMO",
  "REMOVE_FROM_CART",
  "REMOVE_FROM_CART_PROMO_ITEM",
]);

export const isDeliverySlotEligible = (day, slot) =>
  !!day && day.active === true && !!slot && slot.eligible !== false;

export const getFirstEligibleDeliverySlot = (source = {}) => {
  for (const date of Object.keys(source || {})) {
    const day = source[date];
    const slot = (day?.slots || []).find((candidate) =>
      isDeliverySlotEligible(day, candidate)
    );
    if (slot) return { date, slot };
  }
  return null;
};

export const findEligibleDeliverySlot = (
  source = {},
  date,
  start,
  end
) => {
  const day = source?.[date];
  return (day?.slots || []).find(
    (slot) =>
      slot.start == start &&
      slot.end == end &&
      isDeliverySlotEligible(day, slot)
  );
};

export default (state = initialState, { type, payload, key }) => {
  if (CART_CONTENT_ACTIONS.has(type) && state.type === "asap")
    return { ...state, authority: "" };
  let expected_date = null;
  let expected_start = null;
  let expected_end = null;
  let authority = state.authority || "";
  switch (type) {
    case REHYDRATE: {
      // deliveryTime is intentionally not persisted (it contains expiring
      // availability and signed ASAP proofs). Restore only the shopper's
      // selection from the safe v2 context before any cart/checkout mounts.
      const context = payload?.checkoutV2?.fulfillmentContext;
      const time = context?.time;
      if (
        key !== "cart" ||
        state.type ||
        !["delivery", "pickup"].includes(context?.type) ||
        !["asap", "scheduled"].includes(time?.type)
      )
        return state;
      const location = normalizeFulfillmentContext({
        address: payload.customers?.address,
        branch: payload.branch,
      });
      if (
        hasFulfillmentLocationChanged(context, location) ||
        `${context.branchDeliveryChargeId || ""}` !==
          `${location.branchDeliveryChargeId || ""}`
      )
        return state;
      return {
        ...state,
        type: time.type,
        expected_date: time.date || "",
        expected_start: time.start || "",
        expected_end: time.end || "",
        within: time.withinMinutes ?? null,
        isPickup: context.type === "pickup",
        authority: "",
      };
    }
    case "APPLY_CHECKOUT_V2_DELIVERY_TIME":
      return payload && typeof payload === "object"
        ? { ...state, ...payload, loading: false }
        : state;
    case FETCH_DELIVERY_TIME:
      // Never retain booking-only UI metadata when switching to a mixed cart.
      payload = { booking_time: false, booking_error: null, ...payload };
      expected_date = state.expected_date;
      expected_start = state.expected_start;
      expected_end = state.expected_end;
      let newType = state.type;

      if (
        expected_date &&
        expected_start &&
        expected_end &&
        state.type !== "asap"
      ) {
        let slotsToSearchIn = null;
        if (!!payload.pickup && Object.keys(payload.pickup).length > 0) {
          slotsToSearchIn = payload.pickup;
        } else if (
          !!payload.scheduled &&
          Object.keys(payload.scheduled).length > 0
        ) {
          slotsToSearchIn = payload.scheduled;
        }

        if (slotsToSearchIn) {
          if (
            findEligibleDeliverySlot(
              slotsToSearchIn,
              expected_date,
              expected_start,
              expected_end
            )
          ) {
            // let asap = state.asap;
            // if (payload.reset_asap) {
            //   asap = null;
            // }
            // if (payload.asap) asap = payload.asap;
            return {
              ...state,
              ...payload,
              expected_date: expected_date,
              expected_start: expected_start,
              expected_end: expected_end,
              didOpen: false,
            };
          }
        }
      }
      if (
        !!payload.pickup &&
        Object.keys(payload.pickup).length > 0 &&
        !payload.asap
      ) {
        const selection = getFirstEligibleDeliverySlot(payload.pickup);
        expected_date = selection?.date || "";
        expected_start = selection?.slot?.start || "";
        expected_end = selection?.slot?.end || "";
        authority = "";
      } else if (!payload.pickup || payload.asap) {
        if (payload.asap) {
          expected_date = moment(payload.asap.date, "YYYY-MM-DD").format(
            "YYYY-MM-DD"
          );
          expected_start = payload.asap.start;
          expected_end = payload.asap.end;
          authority = payload.asap.authority || "";
          newType = "asap";
        } else if (
          !!payload.scheduled &&
          Object.keys(payload.scheduled).length > 0
        ) {
          const selection = getFirstEligibleDeliverySlot(payload.scheduled);
          expected_date = selection?.date || "";
          expected_start = selection?.slot?.start || "";
          expected_end = selection?.slot?.end || "";
          authority = "";
        }
      }

      if (!newType) {
        if (payload.asap) newType = "asap";
        else newType = "scheduled";
      } else if (newType == "asap" && !payload.asap) newType = "scheduled";

      return {
        ...state,
        ...payload,
        type: newType,
        isPickup: payload.isPickup,
        expected_date: expected_date,
        expected_start: expected_start,
        expected_end: expected_end,
        within:
          newType === "asap" && Number.isFinite(Number(payload.asap?.within))
            ? Number(payload.asap.within)
            : null,
        authority,
        didOpen: false,
      };
    case DELIVERY_TIME_LOADING:
      return { ...state, loading: payload };

    case "HANDLE_DELIVERY_TYPE_CHANGE":
      const source = state.scheduled || state.pickup;
      if (payload == "scheduled") {
        const selection = getFirstEligibleDeliverySlot(source);
        if (!selection) return state;
        expected_date = selection.date;
        expected_start = selection.slot.start;
        expected_end = selection.slot.end;
        authority = "";
      } else if (payload == "asap" && state.asap) {
        expected_date = moment(state.asap.date, "YYYY-MM-DD").format(
          "YYYY-MM-DD"
        );
        expected_start = state.asap.start;
        expected_end = state.asap.end;
        authority = state.asap.authority || "";
      } else {
        return state;
      }
      return {
        ...state,
        type: payload,
        expected_date: expected_date,
        expected_start: expected_start,
        expected_end: expected_end,
        within:
          payload === "asap" && Number.isFinite(Number(state.asap?.within))
            ? Number(state.asap.within)
            : null,
        authority,
      };

    case "HANDLE_DELIVERY_DATE_CHANGE":
      const dateSource = state.isPickup ? state.pickup : state.scheduled;
      const dateDay = dateSource?.[payload];
      const dateSlot = (dateDay?.slots || []).find((slot) =>
        isDeliverySlotEligible(dateDay, slot)
      );
      if (!dateSlot) return state;
      expected_date = payload;
      expected_start = dateSlot.start;
      expected_end = dateSlot.end;
      return {
        ...state,
        expected_date: expected_date,
        expected_start: expected_start,
        expected_end: expected_end,
        within: null,
        authority: "",
      };

    case "HANDLE_DELIVERY_TIME_CHANGE":
      expected_start = payload.split(" - ")[0];
      expected_end = payload.split(" - ")[1];
      const timeSource = state.isPickup ? state.pickup : state.scheduled;
      if (
        !findEligibleDeliverySlot(
          timeSource,
          state.expected_date,
          expected_start,
          expected_end
        )
      ) {
        return state;
      }
      return {
        ...state,
        expected_start: expected_start,
        expected_end: expected_end,
        within: null,
        authority: "",
      };

    case "SET_DELIVERY_TIME_DRAWER":
      return {
        ...state,
        drawerOpen: payload,
        didOpen: payload == true ? true : state.didOpen,
      };

    case "SET_DELIVERY_TIME_DID_OPEN":
      return { ...state, didOpen: payload };

    case "RESET_DELIVERY_TIME":
      return {
        ...initialState,
      };
    case "SHOW_CHECK_CART":
      return { ...state, showCheckCart: true };
    case "CHECKOUT_V2_COMMIT_DELIVERY_TIME":
      return {
        ...state,
        type: ["asap", "scheduled"].includes(payload?.type)
          ? payload.type
          : state.type,
        expected_date: payload?.expected_date || "",
        expected_start: payload?.expected_start || "",
        expected_end: payload?.expected_end || "",
        within:
          payload?.within === null || payload?.within === undefined
            ? null
            : Number(payload.within),
        authority:
          payload?.type === "asap" ? payload?.authority || "" : "",
        isPickup: payload?.isPickup === true,
      };
    default:
      return state;
  }
};
